import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { reportError } from '../lib/errorReporter';
import i18n from '../i18n/config';

/**
 * 전역 에러 경계(ErrorBoundary) 컴포넌트
 * 
 * 렌더링 중 예기치 못한 런타임 오류가 발생했을 때 하얀 화면(Whiteout) 크래시를 방지하고
 * 사용자 친화적인 폴백 화면을 제공하며, 오류를 reportError 창구로 수집합니다.
 * 
 * [주의] i18n 초기화 실패 상황에서도 정상 표시되어야 하므로 다국어 훅 대신 한국어 문구를 직접 하드코딩합니다.
 * [주의] AppLayout 자체 에러 발생 시에도 정상 렌더링되어야 하므로 외부 레이아웃을 독립적인 인라인/Tailwind 구조로 작성합니다.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error, errorInfo) {
    reportError(error, {
      source: 'ErrorBoundary',
      extra: errorInfo,
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="h-[100dvh] bg-[#FFFDF5] w-full flex justify-center overflow-hidden">
          <div className="w-full max-w-[480px] bg-surface relative h-[100dvh] flex flex-col justify-center items-center p-[24px] shadow-2xl">
            <div className="w-full max-w-[400px] flex flex-col items-center text-center">
              <AlertTriangle className="w-[56px] h-[56px] text-error mb-[16px] shrink-0" />
              
              <h1 className="w-full text-[20px] font-bold mb-[8px] text-on-surface break-keep leading-snug">
                {i18n.t('error_boundary.title', '일시적인 문제가 발생했습니다.')}
              </h1>
              
              <p className="w-full text-[14px] text-on-surface-variant mb-[24px] break-keep leading-relaxed">
                {i18n.t('error_boundary.desc', '잠시 후 다시 시도해 주세요.')}
              </p>
              
              <button
                type="button"
                onClick={this.handleReload}
                className="px-[24px] py-[10px] bg-primary text-on-primary rounded-full font-medium text-[14px] shadow hover:opacity-90 active:scale-95 transition-transform cursor-pointer"
              >
                {i18n.t('error_boundary.refresh', '새로고침')}
              </button>

              {import.meta.env.DEV && this.state.error && (
                <div className="w-full mt-[32px] p-[16px] bg-error-container text-on-error-container rounded-[8px] text-left text-[12px] max-w-full overflow-auto font-mono border border-error/20 break-words">
                  <p className="font-bold mb-[4px]">[개발 모드 디버깅 정보]</p>
                  <p className="break-words">{this.state.error.message || String(this.state.error)}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
